import React from 'react';
import { Link } from 'react-router-dom';
import { Cloud, Server, Shield, Zap, Globe, Database, ArrowRight, CheckCircle, Monitor, Lock } from 'lucide-react';
import { useSEO } from '../../hooks/useSEO';
import { Breadcrumb } from '../../components/Breadcrumb';
import { ServiceSchema } from '../../components/SchemaMarkup';
import { useBanner } from '../../contexts/BannerContext';

export const CognitiveCloudPage: React.FC = () => {
  useSEO('cognitive-cloud');
  const { isBannerVisible, bannerHeight } = useBanner();

  const features = [
    {
      icon: <Server className="w-6 h-6" />,
      title: "Infrastructure as Code",
      description: "Automated infrastructure deployment and management using Terraform, CloudFormation, and modern DevOps practices."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Enterprise Security",
      description: "Advanced security measures including encryption, IAM, VPN, and compliance with industry standards like SOC 2 and ISO 27001."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Auto-Scaling",
      description: "Dynamic resource scaling based on demand to optimize performance and costs with intelligent load balancing."
    },
    {
      icon: <Monitor className="w-6 h-6" />,
      title: "24/7 Monitoring",
      description: "Comprehensive monitoring and alerting systems with real-time performance metrics and proactive issue resolution."
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Data Management",
      description: "Secure data storage, backup, and disaster recovery solutions with multi-region replication."
    },
    {
      icon: <Globe className="w-6 h-6" />,
      title: "Global CDN",
      description: "Content delivery networks for optimal performance worldwide with edge computing capabilities."
    }
  ];

  const cloudPlatforms = [
    {
      name: "Amazon Web Services",
      services: ["EC2", "S3", "RDS", "Lambda", "EKS", "CloudFront"]
    },
    {
      name: "Microsoft Azure",
      services: ["Virtual Machines", "Blob Storage", "SQL Database", "Functions", "AKS", "CDN"]
    },
    {
      name: "Google Cloud Platform",
      services: ["Compute Engine", "Cloud Storage", "Cloud SQL", "Cloud Functions", "GKE", "Cloud CDN"]
    }
  ];

  const benefits = [
    "Reduce infrastructure costs significantly",
    "High uptime with service level agreements",
    "Scale resources on-demand",
    "Enhanced data security and compliance",
    "Faster application deployment",
    "Automated backup and disaster recovery",
    "Global availability and performance",
    "24/7 expert support and monitoring"
  ];

  const processes = [
    {
      step: 1,
      title: "Cloud Assessment",
      description: "Analyze your current infrastructure, applications, and requirements to design the optimal cloud architecture."
    },
    {
      step: 2,
      title: "Migration Planning",
      description: "Develop a comprehensive migration strategy with minimal downtime and risk mitigation plans."
    },
    {
      step: 3,
      title: "Implementation",
      description: "Execute the migration and deployment using automated tools and best practices for security and performance."
    },
    {
      step: 4,
      title: "Optimization",
      description: "Continuous monitoring, optimization, and support designed for peak performance and cost-effectiveness."
    }
  ];

  const securityFeatures = [
    { title: "Data Encryption", description: "End-to-end encryption for data at rest and in transit" },
    { title: "Identity Management", description: "Multi-factor authentication and role-based access control" },
    { title: "Network Security", description: "VPC, firewalls, and intrusion detection systems" },
    { title: "Compliance", description: "GDPR, HIPAA, SOC 2, and other regulatory compliance" },
    { title: "Backup & Recovery", description: "Automated backups with point-in-time recovery" },
    { title: "Audit Logging", description: "Comprehensive logging and monitoring for security events" }
  ];

  return (
    <>
      <ServiceSchema
        serviceName="Cognitive Cloud"
        description="Scalable cloud infrastructure and deployment solutions for modern applications"
        serviceType="Cloud Infrastructure"
        url="https://cognitivecrafthub.com/services/cognitive-cloud"
        features={[
          "Infrastructure as Code",
          "Enterprise Security",
          "Auto-Scaling",
          "24/7 Monitoring",
          "Data Management",
          "Global CDN"
        ]}
      />
      <Breadcrumb />
      <main className="pb-20">
      {/* Hero Section */}
      <section 
        className="px-4 sm:px-6 lg:px-8 mb-20"
        style={{ 
          paddingTop: isBannerVisible ? `${bannerHeight + 64 + 44 + 16}px` : '124px' // 64px header + 44px breadcrumb + 16px extra space
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl mb-6">
              <Cloud className="w-10 h-10 text-white" />
            </div>
            <h1 
              className="text-5xl md:text-6xl font-bold mb-8 py-4 px-2 leading-loose bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent overflow-visible min-h-fit"
              style={{ lineHeight: '1.8', minHeight: 'auto', overflow: 'visible', paddingTop: '16px', paddingBottom: '16px' }}
            >
              Cognitive Cloud
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 dark:text-gray-300 mb-8 max-w-4xl mx-auto leading-relaxed">
              Scalable Cloud Infrastructure Solutions
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Build, deploy, and scale your applications with enterprise-grade cloud infrastructure. 
              Our solutions are designed for high uptime, robust security, and seamless scalability.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Cloud Infrastructure Features</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
              Enterprise-grade cloud solutions designed for performance, security, and scalability
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300">
                <div className="text-blue-600 dark:text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">{feature.title}</h3>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cloud Platforms Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Multi-Cloud Expertise</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Certified expertise across major cloud platforms
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {cloudPlatforms.map((platform, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">{platform.name}</h3>
                <div className="space-y-2">
                  {platform.services.map((service, serviceIndex) => (
                    <div key={serviceIndex} className="flex items-center">
                      <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0" />
                      <span className="text-gray-700 dark:text-gray-300 text-sm">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Enterprise Security</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Comprehensive security measures to protect your data and applications
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {securityFeatures.map((security, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-gray-200 dark:border-white/10">
                <div className="flex items-center mb-2">
                  <Lock className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-2" />
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">{security.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-sm">{security.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">Why Choose Cognitive Cloud?</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                  <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="px-4 sm:px-6 lg:px-8 mb-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">Cloud Migration Process</h2>
            <p className="text-xl text-gray-700 dark:text-gray-300">
              Seamless migration to the cloud with minimal downtime
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {processes.map((process, index) => (
              <div key={index} className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white font-bold mr-4">
                    {process.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{process.title}</h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white/5 dark:bg-gradient-to-r dark:from-blue-500/10 dark:to-blue-400/10 rounded-3xl p-12 border border-gray-200 dark:border-white/10">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
              Ready to Move to the Cloud?
            </h2>
            <p className="text-xl text-gray-700 dark:text-gray-300 mb-8 leading-relaxed">
              Transform your infrastructure with scalable, secure, and cost-effective cloud solutions 
              that grow with your business needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
              >
                Start Cloud Migration
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <button
                onClick={() =>
                  window.open(
                    "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                    "_blank"
                  )
                }
                className="px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                Schedule Assessment
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};